// src/pages/ForgotPassword.js
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../api";
import Card from "../components/ui/Card";
import { useSEO } from "../hooks/useSEO";

export default function ForgotPassword() {
  useSEO({
    title: "Forgot Password",
    description: "Reset your password for Predict90. Enter your email to receive a password reset link.",
    keywords: "forgot password, reset password, password recovery",
  });
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!email) return;
    if (Date.now() < cooldownUntil) return;
    setIsSubmitting(true);
    try {
      await API.post("/auth/forgot-password", { email });
      toast.success("If the email exists, a reset link was sent.", {
        autoClose: 4000,
        className: "toast-success-custom"
      });
      // 60s cooldown to avoid double clicks and duplicate emails
      setCooldownUntil(Date.now() + 60 * 1000);
      setTimeout(() => navigate("/auth"), 4000);
    } catch (err) {
      console.error("Forgot password error:", err);
      toast.error(
        err.response?.data?.message || "Could not send reset link.",
        { 
          autoClose: 4000,
          className: "toast-error-custom"
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col">
      <header className="pt-6 md:pt-10">
        <div className="max-w-md mx-auto px-4">
          <img src="/Predict90Wordmark.png" alt="Predict90" className="h-8 w-auto" />
          <h1 className="mt-6 text-[22px] md:text-[28px] font-bold text-ink">Reset your password</h1>
          <p className="mt-1 text-meta text-muted">Enter your email and we'll send a reset link.</p>
        </div>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 py-6">
          <div className="w-full max-w-md">
            <Card title="Forgot Password?" className="w-full">
              <div className="space-y-5">
                <p className="text-gray-700">
                  Enter your account email and we'll send you a password reset link.
                </p>
                <div>
                  <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-all"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !isSubmitting && email && Date.now() >= cooldownUntil) {
                        handleSubmit();
                      }
                    }}
                  />
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !email || Date.now() < cooldownUntil}
                  className={`w-full font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2 ${
                    isSubmitting || !email || Date.now() < cooldownUntil
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-brand text-white hover:bg-brand-hover"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Sending...</span>
                    </>
                  ) : Date.now() < cooldownUntil ? (
                    "Please wait..."
                  ) : (
                    "Send Reset Link"
                  )}
                </button>
                {Date.now() < cooldownUntil && (
                  <p className="text-xs text-gray-500 text-center">
                    You can request another email in a minute.
                  </p>
                )}
                <div className="pt-4 border-t border-gray-200">
                  <Link
                    to="/auth"
                    className="text-sm text-gray-600 hover:text-gray-900 underline text-center block"
                  >
                    Back to Sign In
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </main>

        <ToastContainer 
          position="top-center"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          className="toast-container-custom"
          toastClassName="toast-custom"
          bodyClassName="toast-body-custom"
          progressClassName="toast-progress-custom"
        />
    </div>
  );
}



